/**
 * Utilities for query values that live in the url hash. The AngularJS host routes in the hash,
 * and the standalone app keeps these values in the hash too, so both modes read and write the
 * same place.
 */

export const splitHash = (
  hash: string
): { path: string; params: URLSearchParams } => {
  const queryIndex = hash.indexOf('?')
  if (queryIndex === -1) {
    return { path: hash, params: new URLSearchParams() }
  }
  return {
    path: hash.slice(0, queryIndex),
    params: new URLSearchParams(hash.slice(queryIndex + 1)),
  }
}

export const readHashParam = (key: string): string => {
  return splitHash(window.location.hash).params.get(key) ?? ''
}

/**
 * Set or remove a query value in the url hash without triggering a router navigation, so route
 * loaders do not run again. An empty value removes the key.
 */
export const updateUrlSearchParams = (queryKey: string, queryValue: string) => {
  const { path, params } = splitHash(window.location.hash)
  if (queryValue === (params.get(queryKey) ?? '')) return

  if (queryValue) {
    params.set(queryKey, queryValue)
  } else {
    params.delete(queryKey)
  }

  // Rebuild the hash from the parsed values. set() overwrites, so no duplicate keys like tab=x&tab=x.
  const queryString = params.toString()
  const newHash = queryString ? `${path}?${queryString}` : path

  // Normally with React, you'd update these values via useSearchParams from react-router-dom
  // and the search would update for you. However, that also triggers a navigation to the new
  // url, which runs the route loader again, and we do not want that: all the data is already
  // on the client and we are only filtering what we have. So we call
  // `window.history.replaceState` ourselves to keep the router from running the loader.
  window.history.replaceState(null, '', window.location.pathname + newHash)

  // This tells AngularJS that the url changed in React, mirroring what 'normal' React route
  // changes do when they emit this same event.
  window.dispatchEvent(
    new CustomEvent('@@loaded/reactNavigation', {
      detail: {
        to: window.location.pathname + newHash,
        reactInternal: true,
      },
    })
  )
}
